---
title: sandbox new
description: A generator to get prototyping in React quickly with vite, shadcn, framer-motion, motion-primitives.
linkUrl: https://github.com/matthewlyle/sandbox-new
linkLabel: View on GitHub
linkIcon: github
---

<video 
  src="/tools/sandboxnew/vercel.mp4" 
  className="rounded-lg"
  autoPlay 
  loop 
  muted 
  playsInline
  controls
/>

I fell in love with generators working at an agency in 2014, making white label websites for a variety of clients every week. It was great to start with a common core and not have to write and update boilerplate all the time. 

They've only become more relevant with LLMs. 

Sometimes I work on multiple different prototypes in even a single day, so it's nice to be able to spin up a new sandbox environment as easily as possible.

**This generator has my time-to-sandbox down to a few seconds. Practically, this means **I can spin up a prototype and start visualizing things while in a conversation with somebody.** 

## The "AI Stack"

If you've been paying attention to the front-end world at all lately, these probably aren't news to you:

- [Tailwind](https://tailwindcss.com/) for utility-based CSS 
- [shadcn](https://ui.shadcn.com/) for pre-built, accessible components built ontop of Tailwind 
- [Sonner](https://sonner.emilkowal.ski/) for Toast messages 
- [Vite](https://vite.dev/) for building 
- [wouter](https://github.com/molefrog/wouter) for routing 
- [Cursor](https://cursor.com/) rules

Due to the ubiquity of these packages along with excellent documentation, I find LLMs more adept at working with them. They're also just well made in general, with good accessibility and themeability out of the box so I'm not too hard-pressed to explore other alternatives. 

To learn more about how I customize Tailwinds and Shadcn, see [Extending Tailwind](/thoughts/extending-tailwind). 

If I'm working on many prototypes for the same application, I might make a fork with relevant componentry and styles and use that for the duration of the project.  

## The generator

The generator itself is super simple. It's just a template folder that includes configuration, two pages with a router, and a button that triggers a Toast component. Then there's a folder to store the prototypes in and [Plop](https://plopjs.com/) to copy template files and replace variables. 

<video 
  src="/tools/sandboxnew/intro.mp4" 
  className="breakout rounded-lg"
  autoPlay 
  loop 
  muted 
  playsInline
  controls
/>

## Running the generator 

`npm run new` will ask for a name and create a new prototype sandbox. 

`npm run dev` runs the dev server at localhost:5173, and if it's already taken starts trying other ports until it finds an available one. 

## The host 
Another big boy you've probably heard of: [Vercel](https://vercel.com/). 

They have a generous free tier, and it's easy to upload a new prototype. 

`vercel init` will create a new project. Vite should be auto-detected so it's mostly just pressing ok. 

`vercel --prod` will upload my prototype to be able to be shared with a simple link. 

<video 
  src="/tools/sandboxnew/vercel.mp4" 
  className="rounded-lg"
  autoPlay 
  loop 
  muted 
  playsInline
  controls
/>

[View on GitHub →](https://github.com/matthewlyle/sandbox-new)

[See examples of prototypes I made at Plex →](/resume/plex)